import { StatusTypes } from '@grin-rnd/grin-api-sdk/dist/Enums/Statuses'
import {
  TrackStatusCategories,
  TrackStatusColors,
  TxTrackingListTypes,
  getTrackingReasonColor
} from 'consts/txTrackingReportConsts'
import moment from 'moment'

export const getTxTrackingDateLowerBound = (pastXDays, now) =>
  pastXDays ? moment(now).subtract(pastXDays, 'days').toISOString() : null

/**
 * The category chips of a patient view, built from the aggregation response so the chip counts
 * never change when rows are filtered. Reasons are sorted by patient count, most tagged first,
 * and only reasons tagged in the period get a chip. The streak view has no categories.
 */
export const getTxTrackingCategories = (aggregationData, listType) => {
  if (listType === TxTrackingListTypes.TrackStatus) {
    return TrackStatusCategories.map(category => ({
      ...category,
      count: aggregationData?.trackStatus?.[category.key] || 0,
      color: TrackStatusColors[category.key]
    }))
  }

  if (listType === TxTrackingListTypes.TrackingReasons) {
    return Object.entries(aggregationData?.trackingReasons || {})
      .filter(([, count]) => count > 0)
      .sort(([reasonA, countA], [reasonB, countB]) => countB - countA || reasonA.localeCompare(reasonB))
      .map(([reason, count]) => ({ key: reason, count, color: getTrackingReasonColor(reason) }))
  }

  return []
}

/**
 * The patients count of the All chip: every patient the view lists.
 */
export const getTxTrackingViewTotal = (aggregationData, listType) => {
  switch (listType) {
    case TxTrackingListTypes.TrackStatus:
      return aggregationData?.totalPatients || 0
    case TxTrackingListTypes.TrackingReasons:
      return aggregationData?.patientsWithReasons || 0
    case TxTrackingListTypes.NotOnTrackStreak:
      return aggregationData?.notOnTrackStreak || 0
    default:
      return 0
  }
}

const VIEW_FILTERS = {
  [TxTrackingListTypes.TrackStatus]: {},
  [TxTrackingListTypes.TrackingReasons]: { lastTrackingReasons: { exists: true } },
  [TxTrackingListTypes.NotOnTrackStreak]: { notOnTrackStreak: { eq: true } }
}

const CATEGORY_SEARCH_FIELDS = {
  [TxTrackingListTypes.TrackStatus]: 'lastTrackStatus',
  [TxTrackingListTypes.TrackingReasons]: 'lastTrackingReasons'
}

// A reason chip's key is the reason itself; a track status chip maps to its stored value.
const getCategorySearchValue = (listType, categoryKey) =>
  listType === TxTrackingListTypes.TrackStatus
    ? TrackStatusCategories.find(category => category.key === categoryKey)?.searchValue
    : categoryKey

/**
 * Builds the searchPatientSearchModels filter shared by every Tx Tracking patient view: exact
 * account owner, allowed statuses, a tracked scan (within the selected rolling period when one
 * is set), the view's own condition, and the selected categories with OR semantics.
 */
export const buildTxTrackingPatientsFilter = ({ accountOwnerId, listType, categories = [], pastXDays, now }) => {
  const dateLowerBound = getTxTrackingDateLowerBound(pastXDays, now)

  const filter = {
    doctorId: { eq: accountOwnerId },
    lastTrackedScanDate: dateLowerBound ? { gte: dateLowerBound } : { exists: true },
    ...VIEW_FILTERS[listType],
    and: [
      {
        or: [
          { statusType: { eq: StatusTypes.ActiveTreatment } },
          { statusType: { eq: StatusTypes.Observation } },
          { statusType: { eq: StatusTypes.Retention } },
          { statusType: { eq: StatusTypes.NewLead } }
        ]
      }
    ]
  }

  const categorySearchField = CATEGORY_SEARCH_FIELDS[listType]
  if (categorySearchField && categories.length) {
    filter.and.push({
      or: categories.map(category => ({ [categorySearchField]: { eq: getCategorySearchValue(listType, category) } }))
    })
  }

  return filter
}

/**
 * Parses the recentTrackedScans JSON of a patient search model: the latest tracked scans, newest first.
 */
export const parseRecentTrackedScans = recentTrackedScans => {
  try {
    const parsed = JSON.parse(recentTrackedScans || '[]')
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}
